import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import {
    pickupReset,
    pickupSelectCloseTime,
    pickupSelectDate,
    pickupSelectReadyTime,
    pickupSelectShipments, pickupSetParameter, pickupSubmitRequest
} from '../../../store/pickup/pickup.actions';
import { Validators, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { selectCourierProfile } from '../../../store/profile/profile.state';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, startWith, take, tap } from 'rxjs/operators';
import { PickupParameter } from '../../../models/pickup-parameter';
import {
    selectPickupAvailability,
    selectPickupBusy, selectPickupCourierModule,
    selectPickupDate,
    selectPickupMessages,
    selectPickupReadyTime, selectPickupSuccess
} from '../../../store/pickup/pickup.state';
import { Availability } from '../../../models/availability';
import { Clock } from '../../../utils/clock';
import { PickupBookingService } from '../../shared/services/pickup-booking.service';
import { ServerMessage } from '../../../models/server-message';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { ServerMessageComponent } from '../../shared/server-message/server-message.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { CourierLogoComponent } from '../../shipment/courier-logo/courier-logo.component';

@Component({
    selector: 'app-book-pickup',
    templateUrl: './book-pickup.component.html',
    styleUrls: ['./book-pickup.component.scss'],
    imports: [MatProgressSpinner, ReactiveFormsModule, ServerMessageComponent, MatFormField, MatLabel, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, MatSelect, MatOption, MatError, MatButton, AsyncPipe, MatDialogTitle, MatDialogContent, MatDialogActions, CourierLogoComponent]
})
export class BookPickupComponent implements OnInit, OnDestroy {

    private readonly selectedShipments: string[];

    public parametersGroup = new FormGroup({});
    public date = new FormControl(null, [ Validators.required ]);
    public readyTime = new FormControl('', [ Validators.required ]);
    public closeTime = new FormControl('', [ Validators.required ]);

    currentAvailabilities: Availability[] = [];

    pickupForm = new FormGroup({
        date: this.date,
        readyTime: this.readyTime,
        closeTime: this.closeTime,
        parameters: this.parametersGroup
    });

    courierPickupParameters$: Observable<PickupParameter[]>;
    availableCourierModules$: Observable<string[]>;
    pickupAvailabilities$: Observable<Availability[]>;
    busy$: Observable<boolean>;
    pickupReadyTimes$: Observable<string[]>;
    pickupCloseTimes$: Observable<string[]>;
    messages$: Observable<ServerMessage[]>;
    success: Subscription;
    minimumWindowMinutes = 120;
    invalid$: Observable<boolean> = this.pickupForm.statusChanges.pipe(
        map(() => !this.pickupForm.valid),
        startWith(true)
    );
    courierModule$: Observable<string|null>;

    dateFilter = (d: Date | null): boolean => {
        const dateString = Clock.isoDate(d || new Date());
        let result = false;
        this.currentAvailabilities.forEach(availability => {
            availability.schedule.forEach(schedule => {
                if (schedule.date === dateString) {
                    result = true;
                }
            });
        });
        return result;
    }

    closeTimeValidator = () => {
        if (this.date.value) {
            const date = Clock.isoDate(this.date.value);
            const close = this.closeTime.value;
            const ready = this.readyTime.value;
            const window = Clock.timeToMinutes(close) - Clock.timeToMinutes(ready);
            this.minimumWindowMinutes = BookPickupComponent.minimumWindow(date, this.currentAvailabilities);
            if (window < this.minimumWindowMinutes) {
                return { minimumWindow: true };
            }
        }
        return null;
    }

    constructor(@Inject(MAT_DIALOG_DATA) data: { selectedShipments: string[] }, private dialogRef: MatDialogRef<BookPickupComponent>,
                private store: Store<AppState>, private pickupBookingService: PickupBookingService) {
        this.selectedShipments = data.selectedShipments;
        this.pickupAvailabilities$ = this.store.select(selectPickupAvailability).pipe(
            tap(availabilities => this.currentAvailabilities = availabilities)
        );
        this.courierModule$ = this.store.select(selectPickupCourierModule);
        this.busy$ = this.store.select(selectPickupBusy);
        this.pickupReadyTimes$ = combineLatest([ this.pickupAvailabilities$, this.store.select(selectPickupDate) ]).pipe(
            map(([ availability, date ]) => {
                const times = [];
                availability.forEach(a => a.schedule.filter(s => s.date === date).forEach(s => {
                    let timeOption = s.openTime;
                    const maxTime = Clock.timeToMinutes(s.closeTime) - s.minimumWindowMinutes;
                    while (Clock.timeToMinutes(timeOption) <= maxTime) {
                        times.push(timeOption);
                        timeOption = Clock.advanceTime(timeOption, s.intervalMinutes);
                    }
                }));
                return times;
            })
        );
        this.pickupCloseTimes$ = combineLatest([
            this.pickupAvailabilities$,
            this.store.select(selectPickupDate),
            this.store.select(selectPickupReadyTime)
        ]).pipe(
            map(([ availability, date, readyTime ]) => {
                const times = [];
                if (date && readyTime) {
                    availability.forEach(a => a.schedule.filter(s => s.date === date).forEach(s => {
                        let timeOption = Clock.advanceTime(readyTime, s.minimumWindowMinutes);
                        const maxTime = Clock.timeToMinutes(s.closeTime);
                        while (Clock.timeToMinutes(timeOption) <= maxTime) {
                            times.push(timeOption);
                            timeOption = Clock.advanceTime(timeOption, s.intervalMinutes);
                        }
                    }));
                }
                return times;
            })
        );
        this.messages$ = this.store.select(selectPickupMessages);
        this.pickupForm.addValidators(this.closeTimeValidator);
    }

    ngOnInit(): void {
        this.store.dispatch(pickupReset());
        this.store.dispatch(pickupSelectShipments({ shipmentSelection: this.selectedShipments }));
        this.store.select(selectCourierProfile).pipe(take(1)).subscribe(
            (profile) => {
                profile.couriers.forEach((courier) => {
                    for (const id in courier.pickupParameters) {
                        if (courier.pickupParameters.hasOwnProperty(id)) {
                            const parameter = courier.pickupParameters[id];
                            const control = this.parametersGroup.get(parameter.code);
                            if (control === null) {
                                this.parametersGroup.addControl(parameter.code, new FormControl());
                            }
                        }
                    }
                });
                const example = this.pickupForm.value;
                console.log(example);
            }
        );
        this.availableCourierModules$ = this.pickupAvailabilities$.pipe(
            map((availability) => [ ...new Set(availability.map(a => a.courierModule)) ])
        );
        this.courierPickupParameters$ = combineLatest([
            this.store.select(selectCourierProfile),
            this.availableCourierModules$
        ]).pipe(
            map(([ profile, modules ]) => profile.couriers.filter(c => modules.indexOf(c.code) !== -1)),
            map((couriers) => couriers.map(c => c.pickupParameters).flat()),
            map((sets) => {
                const output = [];
                sets.forEach(s => {
                    const existing = output.find(p => p.code === s.code);
                    if (existing) {
                        existing.choices = [ ...existing.choices.filter(c => !!s.choices.find(p => p.value === c.value)) ];
                    } else {
                        output.push(s);
                    }
                });
                return output;
            })
        );
        this.success = this.store.select(selectPickupSuccess).pipe(
            filter((success) => success === true)
        ).subscribe(() => this.close());
    }

    ngOnDestroy(): void {
        this.success.unsubscribe();
    }

    static minimumWindow(date: string, availabilities: Availability[]): number {
        let minimum = 9999;
        availabilities.forEach((a) => {
            a.schedule.forEach(s => {
                if (s.date === date) {
                    minimum = Math.min(minimum, s.minimumWindowMinutes);
                }
            });
        });
        return minimum;
    }

    close(): void {
        this.dialogRef.close();
    }

    submit(): void {
        this.store.dispatch(pickupSubmitRequest());
    }

    changeDate(): void {
        const date = Clock.isoDate(this.date.value);
        this.store.dispatch(pickupSelectDate({ date }));
    }

    changeReadyTime(): void {
        this.store.dispatch(pickupSelectReadyTime({ readyTime: this.readyTime.value }));
    }

    changeCloseTime(): void {
        this.store.dispatch(pickupSelectCloseTime({ closeTime: this.closeTime.value }));
    }

    changeParameter(p: PickupParameter): void {
        const value = this.parametersGroup.get(p.code).value;
        this.store.dispatch(pickupSetParameter({ name: p.code, value }));
    }
}
